import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import {
  DEV_QUICKCHAT_SUPPORT_BOT_SCENARIO_ID,
  QUICKCHAT_SUPPORT_BOT_SCENARIO_ID,
} from '@/Config/scenarioIds';
import {
  PendingSupportReplyDto,
  SupportReplySeenInput,
  SupportReplySeenResponseDto,
} from '@/Domain/api/App/dto/SupportReplyNotification.dto';
import { User } from '@/Domain/api/User/dto/User.dto';
import { useBootstrapConfig } from '@/Domain/components/App/BootstrapProvider';
import { useWidgetLocationSync } from '@/Domain/components/App/useWidgetLocationSync';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useMobilePreviewStore } from '@/Domain/store/mobilePreview.store';
import {
  SUPPORT_OPERATOR_REPLY_OWNER,
  type SupportAiAutoOpenOwner,
  useSupportWidgetStore,
} from '@/Domain/store/supportWidget.store';
import { getCurrentScenarioId } from '@/context';

const PROD_ASSISTANT_HOST = 'https://app.quickchat.ai';

const ASSISTANT_SCENARIO_ID = import.meta.env.DEV
  ? DEV_QUICKCHAT_SUPPORT_BOT_SCENARIO_ID
  : QUICKCHAT_SUPPORT_BOT_SCENARIO_ID;

const WIDGET_ORIGIN = import.meta.env.VITE_WIDGET_URL || 'https://bubble.quickchat.ai';
const SUPPORT_IDENTITY_MESSAGE = 'quickchat_support_identity';
const SUPPORT_IDENTITY_READY_MESSAGE = 'quickchat_support_identity_ready';
const OPEN_CONVERSATION_MESSAGE = 'quickchat_open_conversation';
const CONVERSATION_OPENED_MESSAGE = 'quickchat_conversation_opened';

// Re-mint before the server's 60-minute token TTL.
const TOKEN_REFETCH_INTERVAL_MS = 45 * 60 * 1000;
const PENDING_SUPPORT_REPLY_POLL_MS = 60 * 1000;

// The full-screen mobile Preview paints over the panel.
const canShowSupportPanel = (): boolean =>
  document.visibilityState === 'visible' && !useMobilePreviewStore.getState().open;

export const SupportWidget = () => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const { apiServerUrl } = useBootstrapConfig();
  const assistantHost = import.meta.env.DEV ? `https://${apiServerUrl}` : PROD_ASSISTANT_HOST;

  const myself = queryClient.getQueryData<User>([Endpoint.user.myself]);
  const {
    open,
    toggleOpen,
    pendingMessage,
    pendingTrigger,
    clearPendingMessage,
    autoOpenTrigger,
    openForOperatorReply,
  } = useSupportWidgetStore();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const paramsReadyRef = useRef(false);
  const supportIdentityReadyRef = useRef(false);
  const pendingReplyRef = useRef<PendingSupportReplyDto | null>(null);
  // Opening and persisting are tracked apart, so a failed seen write retries without reopening.
  const openedReplyMessageIdRef = useRef<number | null>(null);
  const seenRequestedMessageIdRef = useRef<number | null>(null);
  const persistedReplyMessageIdRef = useRef<number | null>(null);
  const postedWhilePanelOpenRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;

  const { data: supportIdentityToken } = useQuery<{ token: string }>({
    queryKey: [QueryKey.app.supportIdentityToken, myself?.id],
    enabled: Boolean(myself),
    staleTime: TOKEN_REFETCH_INTERVAL_MS,
    refetchInterval: TOKEN_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  const { data: pendingReply, dataUpdatedAt: pendingReplyUpdatedAt } =
    useQuery<PendingSupportReplyDto>({
      queryKey: [QueryKey.app.pendingSupportReply, myself?.id],
      enabled: Boolean(myself),
      refetchInterval: PENDING_SUPPORT_REPLY_POLL_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    });

  const { mutate: markSupportReplySeen } = useMutation<
    SupportReplySeenResponseDto,
    unknown,
    SupportReplySeenInput
  >({
    mutationKey: [QueryKey.app.markSupportReplySeen],
    onSuccess: (_response, { message_id }) => {
      persistedReplyMessageIdRef.current = message_id;
      return queryClient.invalidateQueries({ queryKey: [QueryKey.app.pendingSupportReply] });
    },
    onError: () => {
      seenRequestedMessageIdRef.current = null;
    },
  });

  const location = useLocation();
  const currentScenarioId = useMemo(() => getCurrentScenarioId(), [location.pathname]);

  const clientMetadata = useMemo(() => {
    if (myself) {
      const visitor_name = [myself.first_name, myself.last_name].filter(Boolean).join(' ').trim();

      return {
        visitor_name: visitor_name || '',
        visitor_email: myself.email,
        // Later route changes are pushed without reloading the iframe.
        visitor_scenario_id: getCurrentScenarioId(),
      };
    }

    return null;
  }, [myself]);

  const config = useMemo(() => {
    if (!clientMetadata) return null;

    const params = new URLSearchParams({
      scenarioId: ASSISTANT_SCENARIO_ID,
      viewType: 'embed',
      showCloseButton: 'true',
      // Suppress the widget's autofocus on iframe load. The Support AI iframe
      // is mounted globally by `DefaultLayout` and finishes loading 1-2s after
      // any page-level autofocus (e.g. the Getting Started URL input), at
      // which point its `MessageInput` would steal focus from the parent app.
      autoFocus: 'false',
      ...(assistantHost ? { host: assistantHost } : {}),
      params: JSON.stringify(clientMetadata),
    });

    return `${WIDGET_ORIGIN}/widget.html?${params.toString()}`;
  }, [clientMetadata, assistantHost]);

  const postCustomParams = useCallback((data: Record<string, string>) => {
    if (!paramsReadyRef.current) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'quickchat_update_custom_params', data },
      WIDGET_ORIGIN,
    );
  }, []);

  const postSupportIdentity = useCallback((token: string) => {
    if (!supportIdentityReadyRef.current) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: SUPPORT_IDENTITY_MESSAGE, data: { token } },
      WIDGET_ORIGIN,
    );
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === 'quickchat_minimize') {
        toggleOpen();
      } else if (event.data?.type === 'quickchat_custom_params_ready') {
        paramsReadyRef.current = true;
        postCustomParams({ visitor_scenario_id: currentScenarioId });
      } else if (event.data?.type === SUPPORT_IDENTITY_READY_MESSAGE) {
        supportIdentityReadyRef.current = true;
        if (supportIdentityToken?.token) postSupportIdentity(supportIdentityToken.token);
      } else if (event.data?.type === CONVERSATION_OPENED_MESSAGE) {
        const reply = pendingReplyRef.current;
        if (!reply?.conversation_id || reply.message_id === null) return;
        if (event.data.data?.conv_id !== reply.conversation_id) return;
        if (persistedReplyMessageIdRef.current === reply.message_id) return;
        // Seen cancels the email, so an ack that lands unseen is left for a later poll to redo.
        if (!canShowSupportPanel()) return;
        if (postedWhilePanelOpenRef.current && !openRef.current) return;
        if (openedReplyMessageIdRef.current !== reply.message_id) {
          openedReplyMessageIdRef.current = reply.message_id;
          openForOperatorReply();
          capture(PostHogEventEnum.SupportAiAutoOpened, {
            trigger: SUPPORT_OPERATOR_REPLY_OWNER,
            conversation_id: reply.conversation_id,
          });
        }
        if (seenRequestedMessageIdRef.current === reply.message_id) return;
        seenRequestedMessageIdRef.current = reply.message_id;
        markSupportReplySeen({
          conversation_id: reply.conversation_id,
          message_id: reply.message_id,
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    toggleOpen,
    postCustomParams,
    postSupportIdentity,
    supportIdentityToken?.token,
    currentScenarioId,
    openForOperatorReply,
    markSupportReplySeen,
    capture,
  ]);

  // Keep the widget's in-memory credential current for every send.
  useEffect(() => {
    if (supportIdentityToken?.token) {
      postSupportIdentity(supportIdentityToken.token);
    }
  }, [postSupportIdentity, supportIdentityToken?.token]);

  useEffect(() => {
    postCustomParams({ visitor_scenario_id: currentScenarioId });
  }, [postCustomParams, currentScenarioId]);

  // A new src reloads the iframe; declared before the posting effects so they wait for that load.
  useEffect(() => {
    if (iframeRef.current) delete iframeRef.current.dataset.loaded;
  }, [config]);

  useEffect(() => {
    if (!open || !pendingMessage || !iframeRef.current) return;

    const iframe = iframeRef.current;

    const sendMessage = () => {
      iframe.contentWindow?.postMessage(
        {
          type: 'quickchat_send_message',
          // Rides on this one message: it tells Support AI the app forwarded it rather than
          // the user opening this panel, which is what its opening sentence has to explain.
          data: { message: pendingMessage, supportContextTrigger: pendingTrigger ?? undefined },
        },
        WIDGET_ORIGIN,
      );
      clearPendingMessage();
    };

    if (iframe.dataset.loaded) {
      sendMessage();
    } else {
      iframe.addEventListener('load', sendMessage, { once: true });
      return () => iframe.removeEventListener('load', sendMessage);
    }
  }, [open, pendingMessage, pendingTrigger, clearPendingMessage]);

  // Re-posted each poll until seen persists; not keyed on open, so a close never reopens the panel.
  useEffect(() => {
    pendingReplyRef.current = pendingReply ?? null;
    const iframe = iframeRef.current;
    if (!pendingReply?.conversation_id || !iframe) return;
    if (persistedReplyMessageIdRef.current === pendingReply.message_id) return;

    const conversationId = pendingReply.conversation_id;

    const postOpenConversation = () => {
      // Opening marks it seen, so a hidden or covered panel waits for a later poll.
      if (!canShowSupportPanel()) return;
      const allowSwitch = !openRef.current;
      postedWhilePanelOpenRef.current = !allowSwitch;
      iframe.contentWindow?.postMessage(
        { type: OPEN_CONVERSATION_MESSAGE, data: { conv_id: conversationId, allowSwitch } },
        WIDGET_ORIGIN,
      );
    };

    if (iframe.dataset.loaded) {
      postOpenConversation();
    } else {
      iframe.addEventListener('load', postOpenConversation, { once: true });
      return () => iframe.removeEventListener('load', postOpenConversation);
    }
  }, [pendingReply, pendingReplyUpdatedAt]);

  const handleIframeLoad = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.dataset.loaded = 'true';
    }
  }, []);

  // Keyed off the open->closed transition, not a click handler, to catch every close path;
  // toggleOpen clears autoOpenTrigger in the same update, so the owning trigger is held in a ref.
  const dismissTriggerRef = useRef<SupportAiAutoOpenOwner | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    // Tracks the owner while open (null once the user takes the panel over via Ask AI).
    if (open) dismissTriggerRef.current = autoOpenTrigger;
    if (wasOpenRef.current && !open) {
      // toggleOpen drops anything this store still had queued; the widget may be holding a send
      // its composer lock rejected, so tell it to drop that copy too.
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'quickchat_cancel_pending_send' },
        WIDGET_ORIGIN,
      );
      if (dismissTriggerRef.current) {
        capture(PostHogEventEnum.SupportAiAutoOpenDismissed, {
          scenario_id: getCurrentScenarioId(),
          trigger: dismissTriggerRef.current,
        });
        dismissTriggerRef.current = null;
      }
    }
    wasOpenRef.current = open;
  }, [open, autoOpenTrigger, capture]);

  useWidgetLocationSync(iframeRef, WIDGET_ORIGIN);

  if (!myself || !clientMetadata || !config) return null;

  return (
    <>
      {/* Opening is CSS-only on a mounted, autofocus-suppressed iframe, so nothing else tells a screen reader. */}
      <div
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid="support-ai-auto-open-announcement"
      >
        {open && autoOpenTrigger === SUPPORT_OPERATOR_REPLY_OWNER
          ? t('support_ai_operator_reply_announcement')
          : open && autoOpenTrigger
            ? t('support_ai_auto_opened_announcement')
            : ''}
      </div>
      <iframe
        ref={iframeRef}
        id="feedback-quickchat-widget"
        title="Feedback QuickChat Widget"
        src={config}
        onLoad={handleIframeLoad}
        allow="microphone; local-network-access"
        referrerPolicy="strict-origin"
        className={`fixed bottom-0 right-0 z-50 m-[16px] h-[calc(100%-75px)] w-[calc(100%-32px)] max-w-[420px] rounded-xl border border-border bg-card duration-300 shadow-xl ${
          open
            ? 'ease-in-out translate-x-0 opacity-100 scale-100 visible'
            : 'ease-in-out translate-x-full opacity-70 scale-20 invisible'
        }`}
      />
    </>
  );
};
